import { IconUser } from '@tabler/icons-react'

import { ExpenseType, type Transaction as TransactionType } from '@subaio/api-types/budget'
import type { PaymentFrequency as PaymentFrequencyEnum } from '@subaio/api-types/shared'
import { ChevronFlip, FormattedMessage } from '@subaio/components'
import { cases } from '@subaio/utils'

import { ButtonSolid } from 'components/Buttons'
import { Frequency } from 'components/Frequency'
import { Cell, HeaderRow } from 'components/Lists'
import { ProductLogo } from 'components/ProductLogo'

import { Amount } from '../Amount'
import { EmptyChevron } from '../EmptyChevron'
import { Transaction } from '../Transaction'
import { ItemExpandable } from './ItemExpandable'

type Props = {
	id: string
	currency: string
	title: string
	amount?: number
	paymentCost?: number
	paymentFrequency?: PaymentFrequencyEnum
	frequency?: PaymentFrequencyEnum
	averageLastThreeMonths: number
	averageLastTwelveMonths: number
	transactions: Array<TransactionType>
	imageUrl: string | null
	type: ExpenseType
	amountToShow: 'default' | 'threeMonthsAverage' | 'twelveMonthsAverage'

	onRecategorize?: (serviceId: string) => void
}

export const Service = ({
	id,
	title,
	currency = 'DKK',
	amountToShow = 'default',
	amount,
	paymentCost,
	averageLastThreeMonths,
	averageLastTwelveMonths,
	frequency,
	paymentFrequency,
	imageUrl,
	type,
	transactions,
	onRecategorize,
}: Props) => {
	const hasTransactions = transactions.length > 0

	return (
		<ItemExpandable
			renderRow={({ isExpanded, ...rest }) => (
				<HeaderRow {...rest}>
					<Cell textAlign='center'>{hasTransactions ? <ChevronFlip isActive={isExpanded} /> : <EmptyChevron />}</Cell>
					<Cell textAlign='center'>
						{type === ExpenseType.UserProvided ? (
							<IconUser size={32} strokeWidth={1.2} />
						) : (
							<ProductLogo size={32} imageUrl={imageUrl} title={title} />
						)}
					</Cell>
					<Cell shouldExpand>{title}</Cell>
					{onRecategorize && (
						<Cell>
							<ButtonSolid label='Categorize' onClick={() => onRecategorize(id)} />
						</Cell>
					)}
					<Cell textAlign='right'>
						<Amount
							amount={
								type === ExpenseType.UserProvided
									? (paymentCost ?? amount!)
									: cases(amountToShow, {
											default: paymentCost ?? amount!,
											threeMonthsAverage: averageLastThreeMonths,
											twelveMonthsAverage: averageLastTwelveMonths,
										})
							}
							currency={currency}
							userProvidedAmount={null}
							caption={cases(amountToShow, {
								default: (paymentFrequency || frequency) && (
									<div>
										<Frequency frequency={paymentFrequency ?? frequency!} />
									</div>
								),
								threeMonthsAverage: (
									<div>
										<FormattedMessage
											id='flow.budgetEditForm.serviceThreeMonthsAverage'
											defaultMessage='3 months average'
										/>
									</div>
								),
								twelveMonthsAverage: (
									<div>
										<FormattedMessage
											id='flow.budgetEditForm.serviceTwelveMonthsAverage'
											defaultMessage='12 months average'
										/>
									</div>
								),
							})}
						/>
					</Cell>
				</HeaderRow>
			)}
			isDisabled={!hasTransactions}
		>
			{transactions.map((transaction, index) => (
				<Transaction {...transaction} key={index} />
			))}
		</ItemExpandable>
	)
}
